import type { SvgIconComponent } from '@mui/icons-material';
import { Box } from '@mui/material';
import { type Dispatch, type ReactNode, type SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ZodSchema } from 'zod';
import type { Resource } from '../api/resource';
import { useCrud } from '../hooks/useCrud';
import { useFormError } from '../hooks/useFormError';
import { zodFieldErrors } from '../lib/zod-field-errors';
import { ConfirmDialog } from './ConfirmDialog';
import { DataTable, type Column } from './DataTable';
import type { Tone } from './IconTile';
import { FormDialog } from './FormDialog';
import { RowActions } from './RowActions';

/** What a page passes to the form renderer. */
export interface FormRenderProps<TForm> {
  form: TForm;
  /** Accepts a value or an updater, like React's own setter. */
  setForm: Dispatch<SetStateAction<TForm>>;
  /** `{ fieldName: i18nKey }` from the last failed validation. */
  fieldErrors: Record<string, string>;
  /** True while editing an existing row, false while creating. */
  editing: boolean;
}

interface CrudPageProps<TItem, TForm, TCreate, TUpdate> {
  title: string;
  /** The section's icon, shown at the top of the page and in its empty state. */
  icon?: SvgIconComponent;
  tone?: Tone;
  /** React Query cache key for this resource. */
  resourceKey: string;
  api: Resource<TItem, TCreate, TUpdate>;

  /** Columns without the actions column — that one is appended here. */
  columns: Column<TItem>[];
  rowKey: (item: TItem) => string;

  emptyForm: TForm;
  /** Fills the form from an existing row when editing. */
  toForm: (item: TItem) => TForm;
  /** Form state to the raw object validated by the schema. */
  toPayload: (form: TForm, editing: boolean) => unknown;
  schema: ZodSchema;
  /**
   * Schema for updates, when it differs from creation — a user's password, for
   * instance, is required on create and absent on edit.
   */
  updateSchema?: ZodSchema;
  renderForm: (props: FormRenderProps<TForm>) => ReactNode;

  /**
   * Extra validation the schema cannot express, returning
   * `{ field: i18nKey }`. Runs before the schema.
   */
  validate?: (form: TForm) => Record<string, string> | null;

  createTitle: string;
  editTitle: string;
  deleteTitle: string;
  deleteMessage: (item: TItem) => string;

  defaultSortBy?: string;
  filters?: Record<string, string | number | boolean | undefined>;
  toolbar?: ReactNode;
  formMaxWidth?: 'xs' | 'sm' | 'md' | 'lg';
  /** Extra per-row buttons, e.g. "reset password". */
  rowActions?: (item: TItem) => ReactNode;
  /** Makes the whole row clickable, for tables that open a detail screen. */
  onRowClick?: (item: TItem) => void;
}

/**
 * The list-plus-dialog screen every master-data entity needs: paginated table,
 * search, sort, create/edit in a dialog validated by the shared zod schema, and
 * a delete confirmation. Pages supply only what differs.
 */
export function CrudPage<TItem, TForm, TCreate, TUpdate>({
  title,
  icon,
  tone,
  resourceKey,
  api,
  columns,
  rowKey,
  emptyForm,
  toForm,
  toPayload,
  schema,
  updateSchema,
  renderForm,
  validate,
  createTitle,
  editTitle,
  deleteTitle,
  deleteMessage,
  defaultSortBy,
  filters,
  toolbar,
  formMaxWidth = 'sm',
  rowActions,
  onRowClick,
}: CrudPageProps<TItem, TForm, TCreate, TUpdate>) {
  const { t } = useTranslation();
  const crud = useCrud(resourceKey, api, { filters, defaultSortBy });

  const [editing, setEditing] = useState<TItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<TForm>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formError = useFormError();

  const [deleting, setDeleting] = useState<TItem | null>(null);
  const deleteError = useFormError();

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setFieldErrors({});
    formError.clear();
    setFormOpen(true);
  }

  function openEdit(item: TItem) {
    setEditing(item);
    setForm(toForm(item));
    setFieldErrors({});
    formError.clear();
    setFormOpen(true);
  }

  async function submit() {
    formError.clear();

    const extra = validate?.(form);
    if (extra && Object.keys(extra).length > 0) {
      setFieldErrors(extra);
      return;
    }

    const isEditing = Boolean(editing);
    const activeSchema = isEditing ? (updateSchema ?? schema) : schema;
    const parsed = activeSchema.safeParse(toPayload(form, isEditing));
    if (!parsed.success) {
      setFieldErrors(zodFieldErrors(parsed));
      return;
    }

    setFieldErrors({});
    try {
      if (editing) {
        await crud.update.mutateAsync({ id: rowKey(editing), body: parsed.data as TUpdate });
      } else {
        await crud.create.mutateAsync(parsed.data as TCreate);
      }
      setFormOpen(false);
    } catch (error) {
      formError.capture(error);
    }
  }

  async function confirmDelete() {
    if (!deleting) {
      return;
    }
    deleteError.clear();
    try {
      await crud.remove.mutateAsync(rowKey(deleting));
      setDeleting(null);
    } catch (error) {
      deleteError.capture(error);
    }
  }

  const allColumns: Column<TItem>[] = [
    ...columns,
    {
      id: 'actions',
      label: t('common.actions'),
      align: 'right',
      width: rowActions ? 140 : 100,
      render: (row) => (
        // Stops a row-level click from firing when an action is pressed.
        <Box onClick={(event) => event.stopPropagation()}>
          <RowActions onEdit={() => openEdit(row)} onDelete={() => setDeleting(row)}>
            {rowActions?.(row)}
          </RowActions>
        </Box>
      ),
    },
  ];

  return (
    <>
      <DataTable
        title={title}
        icon={icon}
        tone={tone}
        columns={allColumns}
        query={crud.list}
        search={crud.search}
        onSearchChange={crud.setSearch}
        sortBy={crud.sortBy}
        sortDir={crud.sortDir}
        onSort={crud.toggleSort}
        page={crud.page}
        onPageChange={crud.setPage}
        pageSize={crud.pageSize}
        onPageSizeChange={crud.setPageSize}
        rowKey={rowKey}
        onCreate={openCreate}
        toolbar={toolbar}
        onRowClick={onRowClick}
      />

      <FormDialog
        open={formOpen}
        title={editing ? editTitle : createTitle}
        onClose={() => setFormOpen(false)}
        onSubmit={() => void submit()}
        submitting={crud.create.isPending || crud.update.isPending}
        errorKey={formError.errorKey}
        maxWidth={formMaxWidth}
      >
        {renderForm({ form, setForm, fieldErrors, editing: Boolean(editing) })}
      </FormDialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        title={deleteTitle}
        message={deleting ? deleteMessage(deleting) : ''}
        onCancel={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
        submitting={crud.remove.isPending}
        errorKey={deleteError.errorKey}
      />
    </>
  );
}
